import { useQuery } from '@tanstack/react-query'
import { api } from '../../api/client'
import { keys } from '../../api/keys'
import type { AsyncState } from '../../platform/async/Async'
import { fromQuery } from '../../platform/async/fromQuery'
import { monthDays, worstWeekday, type DailySpend } from './dailySpend'
import { monthBounds } from '../transactions/filters'
import { toDayBuckets } from './cashflowWire'
import { CashflowRequestError } from './useCashflow'

export { worstWeekday }
export type { DailySpend }

const browserTz = () => Intl.DateTimeFormat().resolvedOptions().timeZone

async function fetchDaily(month: string, tz: string, signal: AbortSignal): Promise<DailySpend> {
  const { start, end } = monthBounds(month)
  const { data, error, response } = await api.GET('/api/v1/reports/cashflow', {
    params: { query: { granularity: 'day', include: 'buckets', start_date: start, end_date: end, tz } },
    signal,
  })
  if (error !== undefined || data === undefined) throw new CashflowRequestError(response.status)
  const buckets = toDayBuckets(data)
  return monthDays(buckets, month, buckets.unconvertedCount)
}

/**
 * The month's expense per day, as the server reports it (`/reports/cashflow?granularity=day`,
 * backend #528): the client sums nothing here. `live` (surface `transactions`), so never stale.
 * `currency` undefined means the period report has not told us yet: no request.
 */
export function useDailySpend(month: string, currency: string | undefined): AsyncState<DailySpend> {
  const tz = browserTz()
  const query = useQuery({
    queryKey: keys.cashflow.dailySpend(month, currency ?? '', tz),
    queryFn: ({ signal }) => fetchDaily(month, tz, signal),
    enabled: currency !== undefined,
  })
  const state = fromQuery(query, {
    message: () => 'Spending by day could not be loaded.',
    recovery: { label: 'Try again', onAct: () => void query.refetch() },
    isEmpty: (d) => d.days.every((x) => x.spentCents <= 0),
  })
  return currency === undefined ? { status: 'loading' } : state
}
